import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Plus, ArrowLeft, Edit2, Trash2, Save, Package,
  AlertTriangle, Upload, BarChart2, Tag, FileText, Minus, PlusCircle
} from "lucide-react";

const InventoryPage = () => {
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, low_stock: 0, out_of_stock: 0, total_value: 0 });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustValue, setAdjustValue] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    try { const r = await axios.get(`${API}/inventory`); setItems(r.data); } catch (err) { toast.error("Failed to fetch items"); } finally { setLoading(false); }
  }, []);
  const fetchSummary = useCallback(async () => {
    try { const r = await axios.get(`${API}/inventory/summary`); setSummary(r.data); } catch (err) { console.error("Failed to fetch summary:", err); }
  }, []);

  useEffect(() => { fetchItems(); fetchSummary(); }, [fetchItems, fetchSummary]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(items); return; }
    const q = search.toLowerCase();
    setFiltered(items.filter(i =>
      i.item_name.toLowerCase().includes(q) || i.item_code.toLowerCase().includes(q) || (i.category && i.category.toLowerCase().includes(q))
    ));
  }, [items, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await axios.delete(`${API}/inventory/${id}`); toast.success("Item deleted"); setSelectedItem(null); fetchItems(); fetchSummary(); } catch { toast.error("Failed to delete"); }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/inventory/${selectedItem.id}`, editData);
      toast.success("Item updated");
      setEditing(false);
      fetchItems(); fetchSummary();
      const r = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(r.data);
    } catch { toast.error("Failed to update"); }
  };

  const handleAdjust = async () => {
    const adj = parseInt(adjustValue);
    if (isNaN(adj) || adj === 0) { toast.error("Enter a valid adjustment"); return; }
    try {
      await axios.put(`${API}/inventory/${selectedItem.id}/adjust-stock`, { adjustment: adj });
      toast.success("Stock adjusted");
      setAdjustModalOpen(false); setAdjustValue("");
      fetchItems(); fetchSummary();
      const r = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(r.data);
    } catch { toast.error("Failed to adjust stock"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Client-side file validation
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error("Unsupported file format. Please upload .xlsx, .xls, or .csv files.");
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${API}/inventory/upload/excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = r.data;
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.created} items created. ${data.errors.length} row(s) had issues.`, { duration: 5000 });
        // Show first few errors
        data.errors.slice(0, 3).forEach(err => {
          toast.error(`Row ${err.row}: ${err.reason}`, { duration: 4000 });
        });
      } else {
        toast.success(data.message);
      }
      fetchItems(); fetchSummary();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to upload file";
      toast.error(msg, { duration: 5000 });
    }
    finally { setUploading(false); e.target.value = ''; }
  };

  const startEdit = () => {
    setEditData({
      item_name: selectedItem.item_name, item_code: selectedItem.item_code, category: selectedItem.category,
      hsn_code: selectedItem.hsn_code, gst_tax_rate: selectedItem.gst_tax_rate,
      sales_price: selectedItem.sales_price, purchase_price: selectedItem.purchase_price,
      mrp: selectedItem.mrp, low_stock_alert: selectedItem.low_stock_alert,
      low_stock_warning: selectedItem.low_stock_warning, item_description: selectedItem.item_description
    });
    setEditing(true);
  };

  const getStockBadge = (item) => {
    if (item.current_stock <= 0) return { label: "Out Of Stock", bg: "bg-[#FEF2F2]", text: "text-[#DC2626]" };
    if (item.low_stock_warning && item.current_stock <= item.low_stock_alert) return { label: "Low Stock", bg: "bg-[#FFFBEB]", text: "text-[#D97706]" };
    return { label: "In Stock", bg: "bg-[#F0FDF4]", text: "text-[#16A34A]" };
  };

  const fmt = (v) => new Intl.NumberFormat('en-IN').format(v || 0);

  return (
    <div className="flex h-[calc(100vh-60px)]">
      {/* Left sidebar - item list */}
      <div className="w-[260px] border-r border-[#E2E8F0] flex flex-col bg-white shrink-0">
        <div className="p-3 space-y-2 border-b border-[#E2E8F0]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <Input data-testid="inventory-search" placeholder="Search items..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-[12px] bg-[#F8FAFC] border-[#E2E8F0]" />
          </div>
          <div className="flex gap-1.5">
            <Button data-testid="create-item-btn" onClick={() => setCreateModalOpen(true)}
              className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[11px] h-8 gap-1 font-semibold rounded-lg">
              <Plus className="w-3 h-3" /> Create Item
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" />
              <div data-testid="upload-excel-btn" className="flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E2E8F0] text-[11px] text-[#475569] font-medium hover:bg-[#F1F5F9] transition-colors">
                <Upload className="w-3 h-3" /> {uploading ? "..." : "Upload"}
              </div>
            </label>
          </div>
        </div>

        {/* Summary strip */}
        <div className="px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0] flex justify-between text-[10px]">
          <span className="text-[#64748B]">Total: <b className="text-[#1E293B]">{summary.total}</b></span>
          <span className="text-[#D97706]">Low: <b>{summary.low_stock}</b></span>
          <span className="text-[#DC2626]">Out: <b>{summary.out_of_stock}</b></span>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? <p className="text-center py-8 text-[12px] text-[#94A3B8]">Loading...</p> :
           filtered.length === 0 ? <p className="text-center py-8 text-[12px] text-[#94A3B8]">No items found</p> :
           filtered.map(item => {
            const badge = getStockBadge(item);
            const isActive = selectedItem?.id === item.id;
            return (
              <button key={item.id} data-testid={`item-${item.id}`} onClick={() => { setSelectedItem(item); setEditing(false); setActiveTab("details"); }}
                className={`w-full text-left px-3 py-2.5 border-b border-[#F1F5F9] transition-colors ${isActive ? "bg-[#EFF6FF] border-l-[3px] border-l-[#3B82F6]" : "hover:bg-[#F8FAFC] border-l-[3px] border-l-transparent"}`}>
                <p className={`text-[12px] font-medium truncate ${isActive ? "text-[#1D4ED8]" : "text-[#1E293B]"}`}>{item.item_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-[#94A3B8]">{item.item_code || item.category || "No code"}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>{item.current_stock} PCS</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {selectedItem ? (
          <>
            {/* Header */}
            <div className="border-b border-[#E2E8F0] px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button data-testid="back-to-list" onClick={() => setSelectedItem(null)} className="p-1 rounded hover:bg-[#F1F5F9]">
                    <ArrowLeft className="w-5 h-5 text-[#64748B]" />
                  </button>
                  <h1 className="text-[18px] font-bold text-[#1E293B] truncate max-w-md">{selectedItem.item_name}</h1>
                  {(() => { const b = getStockBadge(selectedItem); return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${b.bg} ${b.text}`}>{b.label}</span>; })()}
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="adjust-stock-btn" variant="outline" size="sm" onClick={() => { setAdjustValue(""); setAdjustModalOpen(true); }}
                    className="text-[12px] gap-1.5 border-[#E2E8F0] text-[#475569]">
                    <BarChart2 className="w-3.5 h-3.5" /> Adjust Stock
                  </Button>
                  {editing ? (
                    <Button data-testid="save-item-btn" size="sm" onClick={handleSave}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5 font-semibold">
                      <Save className="w-3.5 h-3.5" /> Save
                    </Button>
                  ) : (
                    <Button data-testid="edit-item-btn" variant="outline" size="sm" onClick={startEdit}
                      className="text-[12px] gap-1.5 border-[#E2E8F0] text-[#475569]">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </Button>
                  )}
                  <button data-testid="delete-item-btn" onClick={() => handleDelete(selectedItem.id)}
                    className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#DC2626]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-[#E2E8F0] px-6">
              {[{ id: "details", label: "Item Details" }, { id: "stock", label: "Stock Details" }, { id: "partywise", label: "Party Wise Report" }, { id: "prices", label: "Party Wise Prices" }].map(tab => (
                <button key={tab.id} data-testid={`inv-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-[13px] font-medium border-b-2 transition-colors ${activeTab === tab.id ? "border-[#3B82F6] text-[#3B82F6]" : "border-transparent text-[#94A3B8] hover:text-[#475569]"}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === "details" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* General Details */}
                  <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm">
                    <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[14px] font-bold text-[#1E293B]">General Details</span>
                    </div>
                    <div className="px-5 py-4 grid grid-cols-2 gap-x-8 gap-y-4">
                      {editing ? (
                        <>
                          <div><Label className="text-[11px] text-[#94A3B8]">Item Name</Label><Input value={editData.item_name} onChange={e => setEditData({...editData, item_name: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Item Code</Label><Input value={editData.item_code} onChange={e => setEditData({...editData, item_code: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Category</Label><Input value={editData.category} onChange={e => setEditData({...editData, category: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Low Stock Alert</Label><Input type="number" value={editData.low_stock_alert} onChange={e => setEditData({...editData, low_stock_alert: Number(e.target.value)})} className="h-9 mt-1 text-[13px]" /></div>
                          <div className="col-span-2"><Label className="text-[11px] text-[#94A3B8]">Description</Label><Textarea value={editData.item_description} onChange={e => setEditData({...editData, item_description: e.target.value})} className="mt-1 text-[13px]" rows={2} /></div>
                        </>
                      ) : (
                        <>
                          <Field label="Item Name" value={selectedItem.item_name} />
                          <Field label="Item Code" value={selectedItem.item_code || "-"} />
                          <Field label="Category" value={selectedItem.category || "-"} />
                          <Field label="Current Stock" value={`${selectedItem.current_stock} PCS`} />
                          <Field label="Stock Value" value={`₹ ${fmt(selectedItem.current_stock * selectedItem.purchase_price)}`} />
                          <Field label="Low Stock Quantity" value={`${selectedItem.low_stock_alert} PCS`} />
                          <Field label="Low Stock Warning" value={selectedItem.low_stock_warning ? "Enabled" : "Disabled"} />
                          <Field label="Item Description" value={selectedItem.item_description || "-"} />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Pricing Details */}
                  <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm">
                    <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[14px] font-bold text-[#1E293B]">Pricing Details</span>
                    </div>
                    <div className="px-5 py-4 grid grid-cols-2 gap-x-8 gap-y-4">
                      {editing ? (
                        <>
                          <div><Label className="text-[11px] text-[#94A3B8]">Sales Price</Label><Input type="number" value={editData.sales_price} onChange={e => setEditData({...editData, sales_price: Number(e.target.value)})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Purchase Price</Label><Input type="number" value={editData.purchase_price} onChange={e => setEditData({...editData, purchase_price: Number(e.target.value)})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">GST Tax Rate (%)</Label><Input type="number" value={editData.gst_tax_rate} onChange={e => setEditData({...editData, gst_tax_rate: Number(e.target.value)})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">HSN Code</Label><Input value={editData.hsn_code} onChange={e => setEditData({...editData, hsn_code: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">MRP</Label><Input type="number" value={editData.mrp} onChange={e => setEditData({...editData, mrp: Number(e.target.value)})} className="h-9 mt-1 text-[13px]" /></div>
                        </>
                      ) : (
                        <>
                          <Field label="Sales Price" value={`₹ ${fmt(selectedItem.sales_price)} ${selectedItem.sales_tax_inclusive ? 'With Tax' : 'Without Tax'}`} />
                          <Field label="Purchase Price" value={`₹ ${fmt(selectedItem.purchase_price)} ${selectedItem.purchase_tax_inclusive ? 'With Tax' : 'Without Tax'}`} />
                          <Field label="MRP" value={selectedItem.mrp ? `₹ ${fmt(selectedItem.mrp)}` : "-"} />
                          <Field label="GST Tax Rate" value={selectedItem.gst_tax_rate ? `${selectedItem.gst_tax_rate}%` : "-"} />
                          <Field label="HSN Code" value={selectedItem.hsn_code || "-"} />
                          <Field label="Disc. on Sales Price" value={selectedItem.gst_tax_rate ? `${selectedItem.gst_tax_rate}% (₹ ${fmt(selectedItem.sales_price / (1 + selectedItem.gst_tax_rate / 100))})` : "-"} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "stock" && (
                <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm p-6">
                  <h3 className="text-[15px] font-bold text-[#1E293B] mb-4">Stock Overview</h3>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-[#F8FAFC] rounded-xl p-4 text-center"><p className="text-[11px] text-[#94A3B8]">Current Stock</p><p className="text-[24px] font-bold text-[#1E293B]">{selectedItem.current_stock}</p><p className="text-[10px] text-[#94A3B8]">PCS</p></div>
                    <div className="bg-[#F8FAFC] rounded-xl p-4 text-center"><p className="text-[11px] text-[#94A3B8]">Stock Value</p><p className="text-[24px] font-bold text-[#1E293B]">₹{fmt(selectedItem.current_stock * selectedItem.purchase_price)}</p></div>
                    <div className="bg-[#F8FAFC] rounded-xl p-4 text-center"><p className="text-[11px] text-[#94A3B8]">Low Stock Alert</p><p className="text-[24px] font-bold text-[#D97706]">{selectedItem.low_stock_alert}</p><p className="text-[10px] text-[#94A3B8]">PCS</p></div>
                    <div className="bg-[#F8FAFC] rounded-xl p-4 text-center"><p className="text-[11px] text-[#94A3B8]">Status</p>{(() => { const b = getStockBadge(selectedItem); return <p className={`text-[16px] font-bold ${b.text} mt-1`}>{b.label}</p>; })()}</div>
                  </div>
                  <Button onClick={() => { setAdjustValue(""); setAdjustModalOpen(true); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] gap-1.5">
                    <BarChart2 className="w-4 h-4" /> Adjust Stock
                  </Button>
                </div>
              )}

              {(activeTab === "partywise" || activeTab === "prices") && (
                <div className="text-center py-16">
                  <Package className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
                  <p className="text-[#94A3B8] text-[15px]">{activeTab === "partywise" ? "Party Wise Report" : "Party Wise Prices"}</p>
                  <p className="text-[#CBD5E1] text-[12px] mt-1">Coming soon</p>
                </div>
              )}
            </div>
          </>
        ) : (
          /* No item selected - full inventory overview */
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-[22px] font-bold text-[#1E293B]">Inventory</h1>
              <p className="text-[13px] text-[#94A3B8]">Select an item from the list to view details</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-[#EFF6FF] flex items-center justify-center"><Package className="w-4 h-4 text-[#3B82F6]" /></div></div>
                <p className="text-[11px] text-[#94A3B8]">Total Items</p><p className="text-[28px] font-bold text-[#1E293B]">{summary.total}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-[#FFFBEB] flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-[#D97706]" /></div></div>
                <p className="text-[11px] text-[#94A3B8]">Low Stock</p><p className="text-[28px] font-bold text-[#D97706]">{summary.low_stock}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-[#FEF2F2] flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-[#DC2626]" /></div></div>
                <p className="text-[11px] text-[#94A3B8]">Out of Stock</p><p className="text-[28px] font-bold text-[#DC2626]">{summary.out_of_stock}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center"><Tag className="w-4 h-4 text-[#16A34A]" /></div></div>
                <p className="text-[11px] text-[#94A3B8]">Total Value</p><p className="text-[28px] font-bold text-[#1E293B]">₹{fmt(summary.total_value)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Adjust Stock Modal */}
      <Dialog open={adjustModalOpen} onOpenChange={setAdjustModalOpen}>
        <DialogContent className="max-w-sm" data-testid="adjust-stock-modal">
          <DialogHeader>
            <DialogTitle className="text-[16px]">Adjust Stock</DialogTitle>
            <DialogDescription>{selectedItem?.item_name} — Current: {selectedItem?.current_stock} PCS</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setAdjustValue(String((parseInt(adjustValue) || 0) - 1))} className="h-10 w-10"><Minus className="w-4 h-4" /></Button>
              <Input data-testid="adjust-value-input" type="number" value={adjustValue} onChange={e => setAdjustValue(e.target.value)} placeholder="e.g. +10 or -5" className="h-10 text-center text-[16px] font-bold" />
              <Button variant="outline" size="icon" onClick={() => setAdjustValue(String((parseInt(adjustValue) || 0) + 1))} className="h-10 w-10"><PlusCircle className="w-4 h-4" /></Button>
            </div>
            <p className="text-[11px] text-[#94A3B8] text-center">New stock: {(selectedItem?.current_stock || 0) + (parseInt(adjustValue) || 0)} PCS</p>
            <Button data-testid="confirm-adjust-btn" onClick={handleAdjust} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white">Confirm Adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Item Modal */}
      <CreateItemModal open={createModalOpen} onClose={(refresh) => { setCreateModalOpen(false); if (refresh) { fetchItems(); fetchSummary(); }}} />
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-[#94A3B8] mb-0.5">{label}</p>
    <p className="text-[13px] font-medium text-[#1E293B]">{value}</p>
  </div>
);

const CreateItemModal = ({ open, onClose }) => {
  const [data, setData] = useState({ item_name: "", item_code: "", category: "", sales_price: 0, purchase_price: 0, current_stock: 0, hsn_code: "", gst_tax_rate: 0, low_stock_alert: 2, item_description: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setData({ item_name: "", item_code: "", category: "", sales_price: 0, purchase_price: 0, current_stock: 0, hsn_code: "", gst_tax_rate: 0, low_stock_alert: 2, item_description: "" });
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.item_name.trim()) { toast.error("Item name is required"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/inventory`, { ...data, sales_price: Number(data.sales_price), purchase_price: Number(data.purchase_price), current_stock: Number(data.current_stock), gst_tax_rate: Number(data.gst_tax_rate), low_stock_alert: Number(data.low_stock_alert) });
      toast.success("Item created");
      onClose(true);
    } catch { toast.error("Failed to create"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-item-modal">
        <DialogHeader>
          <DialogTitle className="text-[16px]">Create New Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Item Name *</Label><Input data-testid="new-item-name" value={data.item_name} onChange={e => set("item_name", e.target.value)} className="h-9 mt-1 text-[13px]" required /></div>
            <div><Label className="text-[11px]">Item Code</Label><Input value={data.item_code} onChange={e => set("item_code", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Category</Label><Input value={data.category} onChange={e => set("category", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">HSN Code</Label><Input value={data.hsn_code} onChange={e => set("hsn_code", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Sales Price</Label><Input type="number" value={data.sales_price} onChange={e => set("sales_price", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Purchase Price</Label><Input type="number" value={data.purchase_price} onChange={e => set("purchase_price", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Current Stock</Label><Input type="number" value={data.current_stock} onChange={e => set("current_stock", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">GST Rate (%)</Label><Input type="number" value={data.gst_tax_rate} onChange={e => set("gst_tax_rate", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Low Stock Alert</Label><Input type="number" value={data.low_stock_alert} onChange={e => set("low_stock_alert", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
          </div>
          <div><Label className="text-[11px]">Description</Label><Textarea value={data.item_description} onChange={e => set("item_description", e.target.value)} className="mt-1 text-[13px]" rows={2} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onClose(false)} className="text-[12px]">Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">{loading ? "Creating..." : "Create Item"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryPage;
